import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createAgeGroup, getAgeGroupById, updateAgeGroup } from '../../services/ageGroupService';
import { AgeGroup } from '../../types/models';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';

const AgeGroupForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState<Omit<AgeGroup, 'age_group_id' | 'created_at'>>({
    group_name: '',
    birth_years: [new Date().getFullYear()]
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAgeGroup = async () => {
      if (id) {
        try {
          setLoading(true);
          const data = await getAgeGroupById(id);
          if (data) {
            setFormData({
              group_name: data.group_name,
              birth_years: data.birth_years
            });
          }
        } catch (error) {
          console.error('Error fetching age group:', error);
          setError('Failed to load age group data');
        } finally {
          setLoading(false);
        }
      }
    };

    if (isEditMode) {
      fetchAgeGroup();
    }
  }, [id, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBirthYearChange = (index: number, value: string) => {
    const year = parseInt(value);
    if (!isNaN(year)) {
      setFormData(prev => {
        const newBirthYears = [...prev.birth_years];
        newBirthYears[index] = year;
        return { ...prev, birth_years: newBirthYears };
      });
    }
  };

  const addBirthYear = () => {
    setFormData(prev => ({
      ...prev,
      birth_years: [...prev.birth_years, new Date().getFullYear()]
    }));
  };

  const removeBirthYear = (index: number) => {
    if (formData.birth_years.length > 1) {
      setFormData(prev => ({
        ...prev,
        birth_years: prev.birth_years.filter((_, i) => i !== index)
      }));
    }
  };

  const validateForm = () => {
    const currentYear = new Date().getFullYear();
    const invalidYears = formData.birth_years.filter(
      year => year < 1900 || year > currentYear
    );

    if (invalidYears.length > 0) {
      setError(`Invalid birth years: ${invalidYears.join(', ')}. Years must be between 1900 and ${currentYear}`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      if (isEditMode && id) {
        await updateAgeGroup(id, formData);
      } else {
        await createAgeGroup(formData);
      }
      
      navigate('/age-groups');
    } catch (error: any) {
      console.error('Error saving age group:', error);
      setError(error.message || 'Failed to save age group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? t('ageGroups.form.editTitle') : t('ageGroups.form.addTitle')}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label htmlFor="group_name" className="block text-sm font-medium text-gray-700">
                {t('ageGroups.form.fields.groupName.label')}
              </label>
              <input
                type="text"
                name="group_name"
                id="group_name"
                required
                value={formData.group_name}
                onChange={handleChange}
                placeholder={t('ageGroups.form.fields.groupName.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('ageGroups.form.fields.birthYears.label')}
              </label>
              <div className="space-y-3">
                {formData.birth_years.map((year, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={year}
                      onChange={(e) => handleBirthYearChange(index, e.target.value)}
                      min={1900}
                      max={new Date().getFullYear()}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                    {formData.birth_years.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeBirthYear(index)}
                        className="p-2 text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addBirthYear}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('ageGroups.form.fields.birthYears.add')}
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/age-groups')}
              className="mr-3 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('ageGroups.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? t('ageGroups.actions.saving') : t('ageGroups.actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgeGroupForm;